<!--
  Created 30.10.2023
  Settings component for proposal show page.
-->
<template>
  <div>
    <SlideOverWithButton
      :slide-open="showModal"
      :order="1"
      is-wide
      button-title="Proposal settings"
      :icon="Cog8ToothIcon"
      @update:slide-open="(e) => showModal = e">
      <template #title>
        <h3 class="text-2xl font-medium leading-6 text-gray-900">
          Proposal settings
        </h3>
      </template>
      <template #content>
        <p
          v-if="isEditable"
          class="max-w-[90%] text-sm">
          Here you can duplicate the proposal, change branding used or other proposal settings set when it was first created.
        </p>
        <p
          v-else
          class="max-w-[90%] text-sm">
          In Read-Only mode you do not have access to change the settings, but you can duplicate the proposal.
        </p>

        <hr class="my-8">

        <div class="mt-8">
          <h2 class="text-lg font-medium">
            Duplicate proposal
          </h2>
          <p class="mt-2 text-sm">
            You can duplicate this proposal to a new project for a new customer.
            <br>
            Click the button to get started.
          </p>
          <button
            class="mt-4 rounded border border-purple-800
        bg-purple-800 px-3 pb-1.5 pt-1 text-sm font-medium text-white
        hover:border-purple-700 hover:bg-purple-700"
            @click="showDuplicateModal = true">
            Duplicate this proposal
          </button>
        </div>

        <hr class="my-8">

        <div v-if="isEditable">
          <h2 class="mb-2 text-lg font-medium">
            Project settings
          </h2>
          <p class="mb-8 text-sm">
            Here you can change the project settings.
          </p>
          <form
            class="w-128"
            @submit.prevent="formUpdate.transform((data) => ({
              client_id: data.client_id,
              project_id: data.project_id,
              brand_id: data.brand?.id,
              range: data.range,
              notes: data.notes,
              pax: data.pax,
              amount: data.amount,
              currency_id: currency?.id ?? null,
            })).put(route('proposals.update', { proposal: proposal.id }), {
              preserveState: false,
              onError: () => {},
            })">
            <div class="grid grid-cols-3 items-center gap-4">
              <label class="block text-xs font-medium text-gray-700"> Proposal branding </label>
              <div class="col-span-2">
                <Listbox
                  v-slot="{ open }"
                  v-model="formUpdate.brand"
                  by="id">
                  <div class="relative mt-2">
                    <ListboxButton
                      :class="open ? 'border-gray-400 bg-gray-100' : 'border-gray-200'"
                      class="relative w-full cursor-default rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left shadow-sm
                  focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75
                  focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300">
                      <span
                        v-if="formUpdate.brand"
                        class="block truncate">{{ formUpdate.brand.name }}</span>
                      <span
                        v-else
                        class="block truncate font-medium">TravelOffice (system)</span>
                      <span
                        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon
                          class="size-5 text-gray-400"
                          aria-hidden="true" />
                      </span>
                    </ListboxButton>

                    <transition
                      leave-active-class="transition duration-100 ease-in"
                      leave-from-class="opacity-100"
                      leave-to-class="opacity-0">
                      <ListboxOptions
                        class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                        <ListboxOption
                          v-if="$page?.props?.team?.brands.length < 1"
                          v-slot="{ active, selected }"
                          :key="null"
                          :value="null"
                          as="template">
                          <li
                            :class="[
                              active ? '!bg-amber-100 text-amber-900' : 'text-gray-900',
                              selected ? 'bg-amber-50 text-amber-900' : 'text-amber-900',
                              'relative flex cursor-default select-none flex-row items-center space-x-4 px-4 py-2',
                            ]">
                            <span>
                              <img
                                class="h-auto w-16 bg-gray-300 p-0.5"
                                src="/img/traveloffice-logo-white.svg"
                                alt="TravelOffice logo">
                            </span>
                            <span
                              :class="[
                                selected ? 'font-bold' : 'font-normal',
                                'block truncate',
                              ]">
                              <i>TravelOffice (system)</i>
                            </span>
                          </li>
                        </ListboxOption>
                        <ListboxOption
                          v-for="brand in $page?.props?.team?.brands"
                          v-slot="{ active, selected }"
                          :key="brand?.id"
                          :value="brand"
                          as="template">
                          <li
                            :class="[
                              active ? '!bg-amber-100 text-amber-900' : 'text-gray-900',
                              selected ? 'bg-amber-50 text-amber-900' : 'text-amber-900',
                              'relative flex cursor-default select-none flex-row items-center space-x-4 px-4 py-2',
                            ]">
                            <span>
                              <img
                                v-if="brand.logoUrl"
                                class="h-auto w-16 bg-gray-300 p-0.5"
                                :src="brand.logoUrl"
                                :alt="brand.name">
                            </span>
                            <span
                              :class="[
                                selected ? 'font-bold' : 'font-normal',
                                'block truncate',
                              ]">
                              {{ brand.name }} {{ brand.default ? '(default)' : '' }}
                            </span>
                          </li>
                        </ListboxOption>
                      </ListboxOptions>
                    </transition>
                  </div>
                </Listbox>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4">
              <label class="block text-xs font-medium text-gray-700"> Branding size </label>
              <div class="col-span-2">
                <Listbox
                  v-slot="{ open }"
                  v-model="brandSize">
                  <div class="relative mt-2">
                    <ListboxButton
                      :class="open ? 'border-gray-400 bg-gray-100' : 'border-gray-200'"
                      class="relative w-full cursor-default rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left shadow-sm
                  focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75
                  focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300">
                      <span
                        v-if="brandSize"
                        class="block truncate capitalize">{{ brandSize }}</span>
                      <span
                        v-else
                        class="block truncate font-medium">Medium</span>
                      <span
                        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon
                          class="size-5 text-gray-400"
                          aria-hidden="true" />
                      </span>
                    </ListboxButton>

                    <transition
                      leave-active-class="transition duration-100 ease-in"
                      leave-from-class="opacity-100"
                      leave-to-class="opacity-0">
                      <ListboxOptions
                        class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                        <ListboxOption
                          v-for="(size, index) in brandSizes"
                          v-slot="{ active, selected }"
                          :key="index"
                          :value="size"
                          as="template">
                          <li
                            :class="[
                              active ? '!bg-amber-100 text-amber-900' : 'text-gray-900',
                              selected ? 'bg-amber-50 text-amber-900' : 'text-amber-900',
                              'relative flex cursor-default select-none flex-row items-center space-x-4 px-4 py-2',
                            ]">
                            <span
                              :class="[
                                selected ? 'font-bold' : 'font-normal',
                                'block truncate',
                              ]">
                              <span class="capitalize">{{ size === 'xl' ? 'XL' : size }}</span> brand size
                            </span>
                          </li>
                        </ListboxOption>
                      </ListboxOptions>
                    </transition>
                  </div>
                </Listbox>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-8">
              <label class="block text-xs font-medium text-gray-700"> Number of travelers </label>
              <div class="col-span-2">
                <input
                  id="pax"
                  v-model="formUpdate.pax"
                  name="pax"
                  type="number"
                  min="0"
                  oninput="validity.valid||(value='')"
                  class="block w-full max-w-lg rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  @change="formUpdate.clearErrors('pax')">
                <p
                  v-if="formUpdate.errors.pax"
                  class="mt-2 text-sm text-red-600">
                  {{ formUpdate.errors.pax }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-8">
              <label class="block text-xs font-medium text-gray-700"> Budget per traveller </label>
              <div class="col-span-2">
                <div class="relative mt-1 flex rounded-md shadow-sm">
                  <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span class="text-sm text-gray-500">
                      {{ currency ? currency.symbol : '$' }}
                    </span>
                  </div>
                  <input
                    id="amount"
                    v-model="formUpdate.amount"
                    type="text"
                    name="amount"
                    placeholder="0.00"
                    autocomplete="username"
                    class="block w-full min-w-0 rounded-none rounded-l-md border-gray-300 pl-11 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    @change="formUpdate.clearErrors('amount')">
                  <span class="inline-flex min-w-32 items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-sm text-gray-500">
                    <CurrencySelectPicker
                      v-model="currency"
                      label=""
                      :borders="false" />
                  </span>
                </div>
                <p
                  v-if="formUpdate.errors.currency_id || formUpdate.errors.amount"
                  class="mt-2 text-sm text-red-600">
                  {{ formUpdate.errors.currency_id }}
                  {{ formUpdate.errors.amount }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-8">
              <label class="block text-xs font-medium text-gray-700"> Travel date (from-to) </label>
              <div class="col-span-2">
                <Datepicker
                  v-model="formUpdate.range"
                  :enable-time-picker="false"
                  range
                  class="w-72"
                  multi-calendars
                  menu-class-name="p-2 !border-2 !border-gray-400 drop-shadow-2xl"
                  :week-numbers="{ type: 'local' }"
                  six-weeks="fair"
                  partial-flow
                  text-input
                  :config="{ keepActionRow: true }"
                  auto-apply
                  :locale="currentLocale"
                  :format="'dd.MM.yyyy'" />
                <p
                  v-if="formUpdate.errors.range"
                  class="mt-2 text-sm text-red-600">
                  {{ formUpdate.errors.range }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-8">
              <label class="block text-xs font-medium text-gray-700"> Notes </label>
              <div class="col-span-2">
                <textarea
                  id="notes"
                  v-model="formUpdate.notes"
                  rows="5"
                  class="block w-full max-w-lg rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  @change="formUpdate.clearErrors('notes')" />
                <p
                  v-if="formUpdate.errors.notes"
                  class="mt-2 text-sm text-red-600">
                  {{ formUpdate.errors.notes }}
                </p>
              </div>
            </div>

            <div class="flex justify-end pt-8">
              <CreateButton
                class="mr-2"
                type="submit"
                :disabled="formUpdate.processing"
                :class="{'cursor-not-allowed opacity-50': formUpdate.processing}">
                Save changes
              </CreateButton>
              <CancelButton
                class="ml-2"
                @click="close" />
            </div>
          </form>
        </div>
      </template>
    </SlideOverWithButton>

    <!-- Duplicate proposal form START -->
    <SimpleModal
      z-index-class="z-[60]"
      box-class="w-11/12 md:w-4/5 lg:w-10/12 max-w-[900px] max-h-[80vh] overflow-y-auto relative z-50"
      :show="showDuplicateModal"
      @update:show="(value) => showDuplicateModal = value">
      <template #content>
        <div class="max-w-3xl p-8">
          <h2 class="text-lg font-medium">
            Duplicate proposal
          </h2>
          <p class="mb-8 text-sm">
            Duplicate this proposal to a new project for a new customer.
          </p>
          <form
            @submit.prevent="submitForm">
            <div class="grid grid-cols-3 items-center gap-4">
              <label class="block text-xs font-medium text-gray-700"> Client <span class="text-red-700">*</span> </label>
              <div class="col-span-2">
                <DynamicDropdown
                  v-model="form.client"
                  required
                  search-url="/api/clients?search="
                  initial-url="/api/clients"
                  placeholder="Select client"
                  :modal="true">
                  <template #modal="{ showModal, query, select, closeModal }">
                    <CreateClientModal
                      z-index-class="z-[65]"
                      :query="query"
                      :show="showModal"
                      @close="closeModal"
                      @created="select" />
                  </template>
                </DynamicDropdown>
                <p
                  v-if="form.errors.client_id"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.client_id }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-8">
              <label class="block text-xs font-medium text-gray-700"> Project <span class="text-red-700">*</span> </label>
              <div class="col-span-2">
                <DynamicDropdown
                  v-model="form.project"
                  required
                  :search-url="'/api/clients/' + form.client?.id + '/projects?search='"
                  :initial-url="'/api/clients/' + form.client?.id + '/projects'"
                  :disabled="!form.client?.id"
                  placeholder="Select projects"
                  :modal="true">
                  <template #modal="{ showModal, query, select, closeModal }">
                    <CreateProjectModal
                      z-index-class="z-[65]"
                      :query="query"
                      :show="showModal"
                      :client="form.client"
                      @close="closeModal"
                      @created="select" />
                  </template>
                </DynamicDropdown>
                <p
                  v-if="form.errors.project_id"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.project_id }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-8">
              <label class="block text-xs font-medium text-gray-700"> Title <span class="text-red-700">*</span> </label>
              <div class="col-span-2">
                <input
                  id="pax"
                  v-model="form.title"
                  required
                  name="title"
                  type="text"
                  oninput="validity.valid||(value='')"
                  class="block w-full max-w-lg rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  @change="form.clearErrors('title')">
                <p
                  v-if="form.errors.title"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.title }}
                </p>
              </div>
            </div>

            <hr class="my-8">

            <div class="grid grid-cols-3 items-center gap-4">
              <label class="block text-xs font-medium text-gray-700"> Travel date (from-to) <span class="text-red-700">*</span> </label>
              <div class="col-span-2">
                <Datepicker
                  v-model="form.range"
                  required
                  :enable-time-picker="false"
                  range
                  class="w-72"
                  multi-calendars
                  menu-class-name="p-2 !border-2 !border-gray-400 drop-shadow-2xl"
                  :week-numbers="{ type: 'local' }"
                  six-weeks="fair"
                  partial-flow
                  text-input
                  :config="{ keepActionRow: true }"
                  auto-apply
                  :locale="currentLocale"
                  :format="'dd.MM.yyyy'" />
                <p
                  v-if="form.errors.range"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.range }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-8">
              <label class="block text-xs font-medium text-gray-700"> Notes </label>
              <div class="col-span-2">
                <textarea
                  id="notes"
                  v-model="form.notes"
                  rows="5"
                  class="block w-full max-w-lg rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  @change="form.clearErrors('notes')" />
                <p
                  v-if="form.errors.notes"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.notes }}
                </p>
              </div>
            </div>

            <hr class="my-8">

            <div class="grid grid-cols-3 items-center gap-4">
              <label class="block text-xs font-medium text-gray-700"> Number of travelers <span class="text-red-700">*</span> </label>
              <div class="col-span-2">
                <input
                  id="pax"
                  v-model="form.pax"
                  required
                  name="pax"
                  type="number"
                  min="0"
                  oninput="validity.valid||(value='')"
                  class="block w-full max-w-lg rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  @change="form.clearErrors('pax')">
                <p
                  v-if="form.errors.pax"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.pax }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-8">
              <label class="block text-xs font-medium text-gray-700"> Budget per traveller <span class="text-red-700">*</span> </label>
              <div class="col-span-2">
                <div class="relative mt-1 flex rounded-md shadow-sm">
                  <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span class="text-sm text-gray-500">
                      {{ currency ? currency.symbol : '$' }}
                    </span>
                  </div>
                  <input
                    id="amount"
                    v-model="form.amount"
                    required
                    type="text"
                    name="amount"
                    placeholder="0.00"
                    class="block w-full min-w-0 rounded-none rounded-l-md border-gray-300 pl-11 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    @change="form.clearErrors('amount')">
                  <span class="inline-flex min-w-32 items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-sm text-gray-500">
                    <CurrencySelectPicker
                      v-model="currency"
                      label=""
                      :borders="false" />
                  </span>
                </div>
                <p
                  v-if="form.errors.currency_id || form.errors.amount"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.currency_id }}
                  {{ form.errors.amount }}
                </p>
              </div>
            </div>

            <hr class="my-8">

            <div class="flex justify-end">
              <CreateButton
                class="mr-2"
                type="submit"
                :disabled="form.processing"
                :class="{'cursor-not-allowed opacity-50': form.processing}">
                <Spinner
                  v-if="form.processing"
                  class="mx-10 size-5"
                  color="white" />
                <template v-else>
                  Duplicate proposal
                </template>
              </CreateButton>
              <CancelButton
                class="ml-2"
                @click="close" />
            </div>
          </form>
        </div>
      </template>
    </SimpleModal>
  </div>
</template>
<script setup>
/* external */
import { Cog8ToothIcon } from '@heroicons/vue/24/outline';
import { useForm } from '@inertiajs/vue3';
import {
  computed, onMounted, ref, watch,
} from 'vue';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/vue';
import {
  CheckIcon, ChevronUpDownIcon,
} from '@heroicons/vue/20/solid';

/* local */
import SlideOverWithButton from '@/Pages/Proposal/Components/SlideOverWithButton.vue';
import DynamicDropdown from '@/ComponentsDeprecated/Select/DynamicDropdown.vue';
import CreateClientModal from '@/ComponentsDeprecated/Modal/CreateClientModal.vue';
import CreateProjectModal from '@/ComponentsDeprecated/Modal/CreateProjectModal.vue';
import Datepicker from '@vuepic/vue-datepicker';
import CreateButton from '@/ComponentsDeprecated/Buttons/CreateButton.vue';
import CancelButton from '@/ComponentsDeprecated/Buttons/CancelButton.vue';
import CurrencySelectPicker from '@/ComponentsDeprecated/Select/CurrencySelectPicker.vue';
import SimpleModal from '@/Components/Modals/SimpleModal.vue';
import { useProposalStore } from '@/Stores/Proposal';
import { router as Inertia } from '@inertiajs/core';
import { notify } from 'notiwind';
import Spinner from '@/Components/Spinner.vue';
import { currentLocale } from '@/utils';

/* *********************************************************************
 * Page declarations
 * ****************************************************************** */
const emit = defineEmits(['update:show']);

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
    default: false,
  },
  isEditable: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const showModal = computed({
  get() {
    return props.show;
  },
  set(value) {
    emit('update:show', value);
  },
});

const showDuplicateModal = ref(false);

const store = useProposalStore();
const proposal = computed(() => store.proposal);

const brandSizes = ref(['small', 'medium', 'large', 'xl']);
const brandSize = computed({
  get() {
    // Get brandSize from the store
    return store.proposal.data.brandSize;
  },
  set(value) {
    // Update brandSize in the store
    store.setBrandSize(value);
  },
});

const currency = ref({
  flag: null,
  id: 'NOK',
  image: '/img/flags/placeholder.png',
  symbol: 'kr',
});

const formUpdate = useForm({
  client_id: proposal.value.client.id,
  project_id: proposal.value.project.id,
  brand: proposal.value.brand,
  range: [
    proposal.value.start_date,
    proposal.value.end_date,
  ],
  notes: proposal.value.notes,
  currency_id: proposal.value.currency_id,
  pax: proposal.value.pax,
  amount: proposal.value.amount_pr_pax,
});

const form = useForm({
  client: proposal.value.client ?? null,
  project: proposal.value.project ?? null,
  title: `${proposal.value.title} (duplicate)` ?? '',
  subtitle: proposal.value.subtitle ?? '',
  range: [
    proposal.value.start_date ?? '',
    proposal.value.end_date ?? '',
  ],
  notes: proposal.value.notes ?? '',
  currency: proposal.value.currency ?? null,
  pax: proposal.value.pax ?? null,
  amount: proposal.value.amount_pr_pax ?? null,
  destinations: proposal.value.destinations ?? [],
  errors: {},
});

/* *********************************************************************
 * Page functions
 * ****************************************************************** */

/**
 * Check if all required fields are filled
 * @returns {null|string} - Error message if not all fields are filled
 */
function areAllFieldsFilled() {
  return form.client && form.project && form.title && form.range && form.pax && form.amount && form.currency;
}

/**
 * Submit the form if all required fields are filled
 */
function submitForm() {
  if (areAllFieldsFilled()) {
    Inertia.post(route('proposals.duplicate', { proposal: proposal.value.id }), {
      client_id: form.client?.id ?? null,
      project_id: form.project?.id ?? null,
      title: form.title ?? '',
      subtitle: proposal.value.subtitle,
      range: form.range,
      notes: form.notes,
      pax: form.pax,
      currency_id: form.currency?.id ?? null,
      amount: form.amount,
    }, {
      errorBag: 'duplicateProposal',
      preserveScroll: false,
      preserveState: false,
      onStart: () => {
        form.processing = true; // Show spinner
      },
      onFinish: () => {
        form.processing = false; // Hide spinner
      },
      onError: (errors) => {
        form.errors = errors;
      },
    });
  } else {
    form.processing = false; // Hide spinner if not all fields are filled
    notify({
      title: 'Please fill out all required fields.',
      type: 'error',
    }, 5000);
  }
}

/**
 *
 */
function close() {
  formUpdate.reset();
  form.reset();

  showDuplicateModal.value = false;
  showModal.value = false;
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

onMounted(() => {
  if (proposal.value.data.brandSize == null) {
    proposal.value.data.brandSize = 'medium';
  }
});

watch(() => form.project, () => {
  if (form.project == null) {
    form.amount = null;
    form.pax = null;
  } else {
    form.amount = form.project.budget_per_traveller;
    form.pax = form.project.total_travelers;
  }
});
watch(() => form.client, () => {
  form.clearErrors('client_id');
});
watch(() => form.project, () => {
  form.clearErrors('project_id');
});
watch(() => form.currency, () => {
  form.clearErrors('currency_id');
});
watch(() => form.range, () => {
  form.clearErrors('range');
});
watch(() => proposal.value.brand, () => {
  if (formUpdate.brand.logoUrl === null) {
    formUpdate.brand = proposal.value.brand;
  }
});

</script>
